import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown, Star, LayoutDashboard } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { navGroupsForRole, type NavItem } from '@/routes/paths';
import { useUIStore } from '@/shared/stores/ui.store';
import { useAuthStore } from '@/shared/stores/auth.store';
import { Button } from '@/shared/components/ui/Button';
import { ZYNX_LUCIDE_MAP } from '@/shared/components/ui/ZynxIcon';
import { effectiveWorkspaceAccess } from '@/shared/permissions/workspaceAccess';
import { grantedSections } from '@/shared/permissions/moduleSections';

/**
 * Favorites were a fully built, tested store (ui.store.ts:
 * addFavorite/removeFavorite) that no component ever called — a real
 * 2026-08-23 audit finding. This renders it: a star toggle on every item,
 * plus a pinned "Favorites" section above the regular nav so a 26-item group
 * like Accounting no longer means scrolling to item 19 every single day.
 */
export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const { user } = useAuthStore();
  const role = user?.role;
  const dept = user?.department;
  const workspaceAccess = effectiveWorkspaceAccess(user);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);
  const groupCollapsed = useUIStore((s) => s.groupCollapsed);
  const toggleGroupCollapsed = useUIStore((s) => s.toggleGroupCollapsed);
  const favorites = useUIStore((s) => s.favorites);
  const addFavorite = useUIStore((s) => s.addFavorite);
  const removeFavorite = useUIStore((s) => s.removeFavorite);

  // One canonical renderer handles permission/role/tier decisions. Department
  // and workspace checks remain presentation scope; the server still decides.
  //
  // The fourth argument is NOT optional in practice. navGroupsForRole runs two
  // gates in order — module (role/department/workspace-access), then section —
  // and omitting `grantedSections` makes the second gate resolve to
  // "unrestricted". That is what made the Roles & Permissions screen appear
  // broken: an administrator would untick screens, the grants were computed,
  // persisted and returned on the user object, and then dropped here, so every
  // menu the user's MODULE allowed stayed on the sidebar. Passing the grants
  // through is the whole fix for the visible symptom.
  //
  // `grantedSections` returns null for superadmins and for accounts created
  // before section grants existed, which navSectionVisible reads as
  // unrestricted — so this narrows nobody who was not deliberately configured.
  const sections = grantedSections(user);
  const actionPermissions = user?.actionPermissions;
  const groups = navGroupsForRole(role, dept, workspaceAccess, sections, actionPermissions, user);

  // Favorites/recents render as this role's own items only — a stale
  // favorite pointing at a screen this user no longer holds simply
  // disappears rather than showing a broken link, self-cleaning on every
  // permission change with no extra bookkeeping.
  const flatItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const itemByPath = useMemo(() => new Map(flatItems.map((item) => [item.path, item])), [flatItems]);
  const favoriteItems = useMemo(
    () => favorites.map((path) => itemByPath.get(path)).filter((item): item is NavItem => !!item),
    [favorites, itemByPath],
  );
  // Undefined groups default to expanded. Persisted booleans are available from
  // synchronous local storage hydration before the first sidebar render.
  const isGroupExpanded = (label: string) => !groupCollapsed[label];

  const toggleGroup = (label: string) => toggleGroupCollapsed(label);

  // COLLAPSED RAIL — the owner's screenshot shows a group label cut to "SAL".
  // Reading the markup as it stood: neither the group header nor the item label
  // is drawn while collapsed (`group.label && !collapsed`, `{!collapsed &&
  // <span>}`). So a half word in a 72px rail is either a stale bundle in the
  // browser or the flex-overflow path that the guard was papering over — a
  // 72px rail leaves a 48px row box and a 13px label does not fit it, which is
  // exactly how "Sales" becomes "SAL". Either way two things were genuinely
  // wrong and both are fixed here rather than hidden again:
  //   1. the label was REMOVED from the DOM while collapsed, so every icon link
  //      in the rail had no accessible name at all (the lucide svg carries
  //      none). The label now always renders, as `sr-only` when collapsed: no
  //      clipped text is possible, and the rail is operable by screen reader.
  //   2. the rail is 68px — 12 (nav px-3) + 12 (link px-3) + 20 (icon) + 12 +
  //      12 — so the icon column is dead-centre in the rail and lands on the
  //      same x in both states. The old 72px rail with `justify-center px-0`
  //      shifted every icon 2px on collapse.
  // Group separation while collapsed is a full-width hairline plus spacing
  // (`role="group"` + `aria-label` carries the name), never a clipped word.
  const renderItem = (item: NavItem, opts: { pinned?: boolean } = {}) => {
    const isFavorite = favorites.includes(item.path);
    return (
      <div key={`${opts.pinned ? 'pin-' : ''}${item.path}`} className="group/navrow relative flex items-center">
        {/* NavLink sets aria-current="page" on the active link itself — do not
            re-implement it here, or the active state would have two sources. */}
        <NavLink
          to={item.path}
          onClick={() => setSidebarOpen(false)}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            cn(
              'group relative flex min-w-0 flex-1 items-center gap-3 rounded-lg px-3 py-2.5 text-data-md font-medium outline-none transition-colors duration-150 ease-standard',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
              // Active = one treatment only: violet-tinted pill, ink-violet label
              // (#5b45d6 on #eeeafb measures 5.9:1, so the active row is legible,
              // not just coloured), plus the accent bar. Hover is the neutral
              // canvas fill, so the two states cannot be confused.
              isActive
                ? 'bg-primary-container font-semibold text-primary'
                : 'text-on-surface-variant hover:bg-surface-variant hover:text-on-surface',
            )
          }
        >
          {({ isActive }) => (
            <>
              {/* Active accent bar. bg-primary, not bg-gradient-brand: the
                  gradient utilities are solid stops now, so the name only
                  misleads the next reader. */}
              <span
                className={cn(
                  'absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary transition-opacity',
                  isActive ? 'opacity-100' : 'opacity-0',
                )}
                aria-hidden
              />
              {/* Crisp, professional Lucide icon: unboxed, uniform, with active and hover transitions */}
              {(() => {
                const IconComponent = item.icon || (item.zynxIcon ? ZYNX_LUCIDE_MAP[item.zynxIcon] : LayoutDashboard);
                return (
                  <IconComponent
                    className={cn(
                      'h-5 w-5 shrink-0 transition-colors duration-150',
                      isActive
                        ? 'text-primary'
                        : 'text-on-surface-variant group-hover:text-on-surface',
                    )}
                    strokeWidth={isActive ? 2 : 1.75}
                    aria-hidden
                  />
                );
              })()}
              <span className={collapsed ? 'sr-only' : 'truncate'}>{item.label}</span>
            </>
          )}
        </NavLink>
        {!collapsed && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isFavorite) removeFavorite(item.path);
              else addFavorite(item.path);
            }}
            aria-label={isFavorite ? `Remove ${item.label} from favorites` : `Add ${item.label} to favorites`}
            aria-pressed={isFavorite}
            className={cn(
              'shrink-0 rounded-lg p-1.5 text-on-surface-subtle opacity-0 transition-opacity group-hover/navrow:opacity-100 focus-visible:opacity-100 hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isFavorite && 'opacity-100',
            )}
          >
            <Star className={cn('h-3.5 w-3.5', isFavorite ? 'fill-amber-400 text-amber-400' : 'text-on-surface-subtle')} />
          </button>
        )}
      </div>
    );
  };

  // Group label. Same type step everywhere (`label-caps` = 11px/700/0.05em) and
  // the same colour: the previous `/70` opacity made it #7e8795 on the rail —
  // 3.66:1, under AA for text this small. on-surface-subtle is #667085, 4.98:1.
  // font-bold is written explicitly because the Button primitive underneath
  // sets font-medium, and a font-weight utility outranks the size token's own
  // weight in the cascade — without it the label lands at 500, not 700.
  const groupLabelClass = 'text-label-caps font-bold uppercase tracking-wider text-on-surface-subtle';

  return (
    <>
      {/* Mobile backdrop. Decorative click target; the drawer itself keeps the
          focus scope and the nav links close it on selection. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-on-surface/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}

      <aside
        data-shell-plane="sidebar"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-outline bg-surface/60 backdrop-blur-xl transition-[width,transform] duration-200 ease-standard lg:static lg:translate-x-0',
          // 68px = 12 (nav) + 12 (link) + 20 (icon) + 12 + 12. The icon column
          // is dead-centre in the rail and does not move when collapsing.
          collapsed ? 'w-[68px]' : 'w-64',
          sidebarOpen ? 'translate-x-0 shadow-surface-2' : '-translate-x-full',
        )}
      >
        {/* Grouped navigation. Spacing between groups is a conditional on
            purpose: in the expanded rail a `space-y` gap is enough, but in the
            collapsed rail the separator is a border on the group itself, and a
            parent `space-y` rule outranks a per-element margin utility, so it
            would have silently won and flattened the hairline rhythm. */}
        <nav aria-label="Primary" className={cn('flex-1 overflow-y-auto px-3 pb-4 pt-2', !collapsed && 'space-y-4')}>
          {favoriteItems.length > 0 && (
            <div className="space-y-1" {...(collapsed ? { role: 'group', 'aria-label': 'Favorites' } : {})}>
              {!collapsed && (
                <div className={cn('flex items-center gap-1.5 px-3 py-1.5', groupLabelClass)}>
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden />
                  <span className="truncate">Favorites</span>
                </div>
              )}
              {favoriteItems.map((item) => renderItem(item, { pinned: true }))}
            </div>
          )}

          {groups.map((group, gi) => (
            <div
              key={group.label ?? `group-${gi}`}
              className={cn(
                'space-y-1',
                // Collapsed separator: a full-width hairline + spacing at every
                // boundary, including the first group sitting under Favorites.
                collapsed && (gi > 0 || favoriteItems.length > 0) && 'mt-3 border-t border-outline pt-3',
              )}
              {...(collapsed && group.label ? { role: 'group', 'aria-label': group.label } : {})}
            >
              {group.label && !collapsed && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleGroup(group.label as string)}
                  aria-expanded={isGroupExpanded(group.label)}
                  aria-controls={`nav-group-${gi}`}
                  className={cn(
                    // A section label, not a button: no hover fill, even though
                    // it is one (the chevron is the affordance).
                    'h-auto w-full justify-between rounded-lg px-3 py-1.5 hover:bg-transparent',
                    groupLabelClass,
                  )}
                >
                  <span className="truncate">{group.label}</span>
                  <ChevronDown
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 transition-transform',
                      isGroupExpanded(group.label) && 'rotate-180',
                    )}
                    aria-hidden
                  />
                </Button>
              )}
              <div id={`nav-group-${gi}`} className="space-y-1">
                {(collapsed || !group.label || isGroupExpanded(group.label)) &&
                  group.items.map((item) => renderItem(item))}
              </div>
            </div>
          ))}
        </nav>


        {!collapsed && (
          <div className={cn('border-t border-outline px-4 py-3', groupLabelClass)}>
            ZYNX · 
            <a
              href="https://www.rbmoilcorporation.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-dotted underline-offset-2 hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              DurraniTech
            </a>
          </div>
        )}
      </aside>
    </>
  );
}
